/**
 * Refetches what the viewer's RLS resolved (ADR 0091) when the viewer's OWN
 * membership changes (office, role or status) in a membership read: cited
 * evidence, knowledge-bucket signed URLs and downloads, knowledge lists, and a
 * document's sources and origin.
 * On-screen entries are reset and refetched, so they show as pending until the
 * refetch answers rather than keep what the previous access resolved; entries
 * off screen are dropped, so their next mount reads afresh (the app does not
 * refetch on mount by default). A read already in flight is cancelled first,
 * so a response authorised before the change never lands after it. A membership refresh that changes nothing, or changes only
 * colleagues' rows or profile fields, does nothing, and changes that arrive
 * together are applied once. Knowledge mutations need no hook here: they are
 * the viewer's own, and the evidence reader rechecks on mount, focus and every
 * minute.
 */
import {
  DRAFT_SOURCE_EVIDENCE_QUERY_KEY,
  KNOWLEDGE_QUERY_KEY,
  SENT_DRAFT_CITATIONS_QUERY_KEY,
} from '@/hooks/pii-query-key-roots';
import { isDocumentProvenanceKey } from '@/hooks/use-document-sources';
import { isKnowledgeStorageKey } from '@/hooks/use-storage-file';

import type { Query, QueryClient } from '@tanstack/react-query';

/** Every query root that reads membership rows (user_id, role, status). */
const MEMBERSHIP_ROOTS = new Set([
  'organization-members-cache',
  'organization-memberships',
  'organization-memberships-by-id',
  'organization-memberships-by-slug',
  'user-organizations',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/**
 * What decides this viewer's access in a membership payload: the offices they
 * belong to, with role and status. Null when the payload is not a row list.
 */
function ownMembershipProjection(data: unknown, actorId: string): string | null {
  if (!Array.isArray(data)) {
    return null;
  }
  return data
    .filter((row): row is Record<string, unknown> => isRecord(row) && row.user_id === actorId)
    .map((row) => `${text(row.organization_id)}|${text(row.role)}|${text(row.status)}`)
    .sort((a, b) => a.localeCompare(b))
    .join('\n');
}

/** Entries whose content follows the viewer's access. */
function isAccessScopedEntry(query: Query): boolean {
  const key = query.queryKey;
  return (
    key[0] === DRAFT_SOURCE_EVIDENCE_QUERY_KEY ||
    key[0] === SENT_DRAFT_CITATIONS_QUERY_KEY ||
    key[0] === KNOWLEDGE_QUERY_KEY ||
    isKnowledgeStorageKey(key) ||
    isDocumentProvenanceKey(key)
  );
}

async function refetchAccessScopedEntries(
  client: QueryClient,
  actorId: string,
  isActive: () => boolean,
): Promise<void> {
  const predicate = (query: Query): boolean =>
    isActive() && isAccessScopedEntry(query) && query.queryKey.includes(actorId);
  // Cancelled first: a read in flight may predate the change.
  await client.cancelQueries({ predicate });
  if (!isActive()) {
    return;
  }
  client.removeQueries({
    predicate: (query) => predicate(query) && query.getObserversCount() === 0,
  });
  if (!isActive()) {
    return;
  }
  await client.resetQueries({ predicate });
}

export function subscribeToEvidenceInvalidation(
  client: QueryClient,
  actorId: string | null,
  onAccessChange?: () => void,
): () => void {
  if (actorId === null) {
    return () => {
      /* nothing was subscribed */
    };
  }
  // Kept after a membership query is evicted, so the read that repopulates it
  // is still compared with what this viewer's access was before.
  const seen = new Map<string, string | null>();
  let scheduled = false;
  let active = true;

  // Reads cached before this subscription are the baseline a later change is
  // measured against.
  for (const root of MEMBERSHIP_ROOTS) {
    for (const query of client.getQueryCache().findAll({ queryKey: [root] })) {
      if (query.state.data !== undefined) {
        seen.set(query.queryHash, ownMembershipProjection(query.state.data, actorId));
      }
    }
  }

  const schedule = (): void => {
    if (scheduled) {
      return;
    }
    scheduled = true;
    onAccessChange?.();
    queueMicrotask(() => {
      scheduled = false;
      if (active) {
        refetchAccessScopedEntries(client, actorId, () => active).catch(() => {
          /* A failed refetch surfaces through each query's own error state. */
        });
      }
    });
  };

  const unsubscribe = client.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated' || event.action.type !== 'success') {
      return;
    }
    const root = (event.query.queryKey as readonly unknown[])[0];
    if (typeof root !== 'string' || !MEMBERSHIP_ROOTS.has(root)) {
      return;
    }
    const projection = ownMembershipProjection(event.query.state.data as unknown, actorId);
    const known = seen.has(event.query.queryHash);
    const previous = seen.get(event.query.queryHash);
    seen.set(event.query.queryHash, projection);
    if (known && previous !== projection) {
      schedule();
    }
  });

  return () => {
    active = false;
    unsubscribe();
  };
}
